import { type TaskBaseStatusEnum, VISIBLE_STATUSES_IN_LEAD_DETAILS } from '@app/data/tasks/constants';
import tasksService from '@app/services/task/tasksService';
import { type AppDispatch } from '@app/store/index';
import { type ListTasksResponseType, type TaskType } from '@queries/task/listTasks';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { GLOBAL_TAG_TYPES } from '@repo/services';
import dayjs from 'dayjs';

export interface TaskTabState {
  tasks: Record<string, TaskType[]>;
  lastLoadedTaskId?: string;
  paginationConfig: {
    hasNextPage: boolean;
    endCursor?: string;
  };
  tasksForUuid?: string;
}

const initialState: TaskTabState = {
  tasks: {},
  lastLoadedTaskId: undefined,
  paginationConfig: {
    hasNextPage: true,
    endCursor: undefined,
  },
  tasksForUuid: undefined,
};

export const tasksTabSlice = createSlice({
  name: 'tasksTab',
  initialState,
  reducers: {
    resetLoadedTasks: (state) => {
      state.tasks = initialState.tasks;
      state.lastLoadedTaskId = initialState.lastLoadedTaskId;
      state.paginationConfig = initialState.paginationConfig;
      state.tasksForUuid = undefined;
    },
    validateAndResetLoadedTasks: (state, action: PayloadAction<string>) => {
      if (state.tasksForUuid !== action.payload) {
        tasksTabSlice.caseReducers.resetLoadedTasks(state);
      }
    },
    addLoadedTasks: (state, action: PayloadAction<{ taskData: ListTasksResponseType; leadUuid: string }>) => {
      const { taskData, leadUuid } = action.payload;
      const {
        tasks,
        pageInfo,
      } = taskData;

      if (state.tasksForUuid !== leadUuid) {
        tasksTabSlice.caseReducers.resetLoadedTasks(state);
      }
      tasks.forEach((task) => {
        const timeKey = dayjs(task.endTime).format('MMMM YYYY');
        const tasksForTimeKey = state.tasks[timeKey] ?? [];
        if (!tasksForTimeKey.find((t) => t.id === task.id)) {
          tasksForTimeKey.push(task);
        }
        state.tasks[timeKey] = tasksForTimeKey;
      });
      const visibleTasks = tasks.filter((t) => VISIBLE_STATUSES_IN_LEAD_DETAILS.includes(t.status));
      state.lastLoadedTaskId = visibleTasks[visibleTasks.length - 1]?.id ?? undefined;
      state.tasksForUuid = leadUuid;
      state.paginationConfig = pageInfo;
    },
    updateTaskStatus: (state, action: PayloadAction<{ taskId: string; status: TaskBaseStatusEnum }>) => {
      const { taskId, status } = action.payload;

      Object.entries(state.tasks).forEach(([timeKey, tasksForTimeKey]) => {
        const taskIndex = tasksForTimeKey.findIndex((t) => t.id === taskId);
        if (taskIndex !== -1 && state.tasks[timeKey]?.[taskIndex]) {
          state.tasks[timeKey][taskIndex].status = status;
        }
      });

      if (state.lastLoadedTaskId === taskId && !VISIBLE_STATUSES_IN_LEAD_DETAILS.includes(status)) {
        state.lastLoadedTaskId = undefined;
      }
    },
  },
});

export const {
  resetLoadedTasks,
  addLoadedTasks,
  updateTaskStatus,
  validateAndResetLoadedTasks,
} = tasksTabSlice.actions;

export const resetLoadedTasksThunk = () => (dispatch: AppDispatch) => {
  dispatch(resetLoadedTasks());
  dispatch(tasksService.util.invalidateTags([GLOBAL_TAG_TYPES.taskList]));
};

export default tasksTabSlice.reducer;
